import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { ApiService } from '../services/api';
import { Area, Servicio } from '../types';
import { X, Building, Users, Briefcase, Grid, User2, Folder, Calendar, TrendingUp } from 'lucide-react';
import Organigrama from './Organigrama';
import PersonalCard from './PersonalCard';
import DevOpsCapacityCard from './DevOpsCapacityCard';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

interface AreaDetailModalProps {
  area: Area;
  isOpen: boolean;
  onClose: () => void;
}

const AreaDetailModal: React.FC<AreaDetailModalProps> = ({ area, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'organigrama' | 'individual' | 'proyectos'>('organigrama');
  
  // Usar endpoint directo de personal filtrado por área en lugar de getAreaById que no funciona
  const { data: personalData, loading: personalLoading, error: personalError } = useApi(
    () => {
      console.log(`🔍 AreaDetailModal: Cargando personal para área ${area.id}`);
      return ApiService.getPersonal({ areaId: area.id });
    },
    [area.id, isOpen]
  );

  // Cargar proyectos del área
  const { data: proyectosData, loading: proyectosLoading, error: proyectosError } = useApi(
    () => {
      console.log(`📁 AreaDetailModal: Cargando proyectos para área ${area.id}`);
      return ApiService.getProyectosByArea(area.id);
    },
    [area.id, isOpen]
  );

  // Crear un objeto areaDetail simulado con el personal cargado
  const areaDetail = personalData ? {
    ...area,
    personal: personalData,
    servicios: [] as Servicio[] // Por ahora servicios vacíos hasta que funcione la API
  } : null;

  const loading = personalLoading;
  const error = personalError;

  // Debug logs
  console.log('🏢 AreaDetailModal State:', {
    areaId: area.id,
    isOpen,
    loading,
    error,
    hasAreaDetail: !!areaDetail,
    personalCount: personalData?.length || 0
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-neutral-900/50 dark:bg-black/70 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-white dark:bg-neutral-900 rounded-lg shadow-md">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-12 h-12 rounded-md flex items-center justify-center mr-4"
                  style={{ color: area.color || '#2563EB' }}
                >
                  <Building size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">
                    {area.nombre}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                    {area.descripcion}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-all"
              >
                <X size={24} className="text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>

            {/* Quick Stats */}
            <div className="flex space-x-6 mt-4">
              <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                <Briefcase size={16} className="mr-2 text-neutral-400 dark:text-neutral-500" />
                <span>{area.serviciosCount || 0} servicios</span>
              </div>
              <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                <Users size={16} className="mr-2 text-neutral-400 dark:text-neutral-500" />
                <span>{area.personalCount || 0} miembros del equipo</span>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="px-6 border-b border-neutral-200 dark:border-neutral-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('organigrama')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all ${
                  activeTab === 'organigrama'
                    ? 'border-primary text-primary dark:text-primary-dark'
                    : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-600'
                }`}
              >
                <Grid size={16} className="mr-2" />
                Organigrama
              </button>
              <button
                onClick={() => setActiveTab('individual')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all ${
                  activeTab === 'individual'
                    ? 'border-primary text-primary dark:text-primary-dark'
                    : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-600'
                }`}
              >
                <User2 size={16} className="mr-2" />
                Individual
              </button>
              <button
                onClick={() => setActiveTab('proyectos')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all ${
                  activeTab === 'proyectos'
                    ? 'border-primary text-primary dark:text-primary-dark'
                    : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-600'
                }`}
              >
                <Folder size={16} className="mr-2" />
                Proyectos
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
            {loading && <LoadingSpinner message="Cargando información del área..." />}
            
            {error && (
              <ErrorMessage 
                message={error} 
                onRetry={() => window.location.reload()} 
              />
            )}

            {areaDetail && (
              <div className="space-y-6">
                {activeTab === 'organigrama' && (
                  <>
                    {/* Organigrama */}
                    <div>
                      <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-4 flex items-center">
                        <Users className="mr-2 text-neutral-400 dark:text-neutral-500" size={20} />
                        Organigrama del Equipo
                      </h4>
                      <Organigrama
                        personal={areaDetail.personal}
                        areaColor={area.color}
                      />
                    </div>

                    {/* Servicios */}
                    {areaDetail.servicios.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-4 flex items-center">
                          <Briefcase className="mr-2 text-neutral-400 dark:text-neutral-500" size={20} />
                          Servicios y Tecnologías
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {areaDetail.servicios.map(servicio => (
                            <div
                              key={servicio.id}
                              className="card p-4"
                            >
                              <h5 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
                                {servicio.tecnologia}
                              </h5>
                              {servicio.categoria && (
                                <span className="badge text-xs mb-2">
                                  {servicio.categoria}
                                </span>
                              )}
                              {servicio.casosUso && (
                                <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
                                  {servicio.casosUso}
                                </p>
                              )}
                              {servicio.equipo && servicio.equipo.length > 0 && (
                                <div className="mt-2">
                                  <div className="flex items-center text-xs text-neutral-500 dark:text-neutral-400">
                                    <Users size={12} className="mr-1 text-neutral-400 dark:text-neutral-500" />
                                    <span>{servicio.equipo.length} miembros asignados</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'individual' && (
                  <div>
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-4 flex items-center">
                      <User2 className="mr-2 text-neutral-400 dark:text-neutral-500" size={20} />
                      Vista Individual del Equipo
                    </h4>

                    {areaDetail.personal.length === 0 ? (
                      <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                        <Users size={48} className="mx-auto mb-4 text-neutral-300 dark:text-neutral-600" />
                        <p>No hay personal asignado a esta área</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {areaDetail.personal.map(person => (
                          <div key={person.id} className="space-y-4">
                            <PersonalCard
                              personal={person}
                              layoutMode="grid"
                            />
                            {person.email && (
                              <div className="card p-4">
                                <DevOpsCapacityCard
                                  userEmail={person.email}
                                  userName={`${person.nombre} ${person.apellido || ''}`}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'proyectos' && (
                  <div>
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-4 flex items-center">
                      <Folder className="mr-2 text-neutral-400 dark:text-neutral-500" size={20} />
                      Proyectos del Área
                    </h4>

                    {proyectosLoading && <LoadingSpinner message="Cargando proyectos..." />}
                    {proyectosError && <ErrorMessage message={proyectosError} />}

                    {!proyectosLoading && !proyectosError && (
                      <>
                        {!proyectosData || proyectosData.length === 0 ? (
                          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                            <Folder size={48} className="mx-auto mb-4 text-neutral-300 dark:text-neutral-600" />
                            <p>No hay proyectos asignados a esta área</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {proyectosData.map(proyecto => {
                              const getEstadoColor = (estado: string) => {
                                switch (estado) {
                                  case 'Planificación':
                                    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
                                  case 'En Progreso':
                                    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
                                  case 'Completado':
                                    return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
                                  default:
                                    return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';
                                }
                              };

                              const formatFecha = (fecha: string) => {
                                return new Date(fecha).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                });
                              };

                              return (
                                <div key={proyecto.id} className="card p-4">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <h5 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-1">
                                        {proyecto.nombre}
                                      </h5>
                                      {proyecto.descripcion && (
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                                          {proyecto.descripcion}
                                        </p>
                                      )}
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(proyecto.estado)}`}>
                                      {proyecto.estado}
                                    </span>
                                  </div>

                                  <div className="flex flex-wrap gap-4 text-sm text-neutral-600 dark:text-neutral-400 mb-3">
                                    {proyecto.clienteEmpresa && (
                                      <div className="flex items-center">
                                        <Building size={14} className="mr-1 text-neutral-400" />
                                        {proyecto.clienteEmpresa}
                                      </div>
                                    )}
                                    <div className="flex items-center">
                                      <Calendar size={14} className="mr-1 text-neutral-400" />
                                      {formatFecha(proyecto.fechaInicio)}
                                      {proyecto.fechaFin && ` → ${formatFecha(proyecto.fechaFin)}`}
                                    </div>
                                  </div>

                                  {proyecto.servicios.length > 0 && (
                                    <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
                                      <div className="flex items-center text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                        <TrendingUp size={14} className="mr-1 text-neutral-400" />
                                        Servicios Utilizados ({proyecto.servicios.length})
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {proyecto.servicios.map(ps => (
                                          <div
                                            key={ps.id}
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 dark:bg-neutral-800 rounded text-xs"
                                          >
                                            <span className="text-neutral-900 dark:text-neutral-50">
                                              {ps.servicio.tecnologia}
                                            </span>
                                            {ps.cantidadUsos > 0 && (
                                              <span className="text-neutral-500 dark:text-neutral-400">
                                                ({ps.cantidadUsos} usos)
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 rounded-b-lg">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaDetailModal;